import React, { useState, useEffect } from 'react';
import { authClient, organizationClient } from '../../lib/auth-client';

interface InviteAcceptanceFormProps {
  invitationId: string;
  onAccepted?: () => void;
}

interface InvitationInfo {
  id: string;
  email: string;
  role: string;
  organizationId: string;
  organizationName: string;
  organizationSlug: string;
  inviterEmail: string;
  status: string;
  expiresAt: string;
}

const InviteAcceptanceForm: React.FC<InviteAcceptanceFormProps> = ({
  invitationId,
  onAccepted,
}) => {
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  // Fetch invitation details so we can show org name, inviter, etc.
  useEffect(() => {
    let cancelled = false;
    const fetchInvitation = async () => {
      setLoading(true);
      setError(null);
      try {
        // @ts-ignore - Better Auth organization plugin types
        const { data, error: fetchError } = await authClient.organization.getInvitation({
          query: { id: invitationId },
        });
        if (cancelled) return;
        if (fetchError) {
          setError(fetchError.message || 'Failed to load invitation details');
        } else if (data) {
          setInvitation(data as unknown as InvitationInfo);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load invitation');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInvitation();
    return () => { cancelled = true; };
  }, [invitationId]);

  const handleAccept = async () => {
    setAccepting(true);
    setError(null);
    try {
      // @ts-ignore - Better Auth organization plugin types
      const { data: acceptData, error: acceptError } = await authClient.organization.acceptInvitation({
        invitationId,
      });
      if (acceptError) {
        setError(acceptError.message || 'Failed to accept invitation');
        setAccepting(false);
        return;
      }

      // Set the newly joined organization as active so the session is updated
      if (invitation?.organizationId) {
        try {
          // @ts-ignore - Better Auth organization plugin types
          await authClient.organization.setActive({
            organizationId: invitation.organizationId,
          });
        } catch (setActiveErr) {
          console.warn('Failed to set active organization after accepting invite:', setActiveErr);
          // Non-fatal — user can still proceed; org will be set on next session
        }
      }

      setAccepted(true);
      onAccepted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tertiary" />
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="bg-error/5 border border-error/20 p-6 text-center">
        <span className="material-icons text-red-400 text-3xl mb-2 block">error_outline</span>
        <p className="text-red-400 text-sm font-medium">Invalid or Expired Invitation</p>
        <p className="text-on-surface-variant/40 text-xs mt-1">{error}</p>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="text-center py-6">
        <span className="material-icons-outlined text-4xl text-emerald-400 block mb-3">check_circle</span>
        <p className="text-on-surface font-medium">Invitation Accepted!</p>
        <p className="text-on-surface-variant/50 text-sm mt-1">
          You've joined {invitation?.organizationName || 'the organization'}.
        </p>
      </div>
    );
  }

  const roleLabel =
    invitation?.role === 'admin' || invitation?.role === 'owner'
      ? 'Admin'
      : invitation?.role === 'member'
        ? 'Member'
        : invitation?.role || 'Member';

  return (
    <div>
      {/* Invitation Details */}
      {invitation && (
        <div className="bg-surface-container-low border border-outline-variant/20 p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="material-icons-outlined text-tertiary">business</span>
            <div>
              <p className="text-sm font-display font-semibold text-on-surface">
                {invitation.organizationName}
              </p>
              <p className="text-xs text-on-surface-variant/40 font-mono">
                {invitation.organizationSlug}
              </p>
            </div>
          </div>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="material-icons text-on-surface-variant/40 text-sm">person_outline</span>
              <span className="text-on-surface-variant/60">Invited by: {invitation.inviterEmail}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-icons text-on-surface-variant/40 text-sm">admin_panel_settings</span>
              <span className="text-on-surface-variant/60">Role: {roleLabel}</span>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-error/5 border border-error/20 p-3 mb-4">
          <p className="text-red-400 text-xs">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={handleAccept}
          disabled={accepting}
          className="btn btn-primary flex-1 gap-1"
        >
          <span className="material-icons text-sm">check</span>
          {accepting ? 'Accepting...' : 'Accept Invitation'}
        </button>
      </div>
    </div>
  );
};

export default InviteAcceptanceForm;